import React, { useEffect, useState } from "react";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import Cell from "./Cell";
import { IExtraTypeUIProps } from "./tableTemplate";
import Modal from "../../Components/Modal/Modal";
import CircularProgress from "@mui/material/CircularProgress";
import { ITableReport } from "../../Interfaces/TableInterfaces";
import { packetErrorHandler } from "../../Utils/requestUtils";
import { WrappedTimeSeriesChart } from "../../Components/Timeseries/WrappedTimeSeriesChart";
import { Graph_Dto } from "../../Interfaces/orderbookGraphInterfaces";
import useSearchParam from "../../Hooks/useSearchParam";

interface ISPProps<SearchParam> {
  searchParams: SearchParam;
  onSubmit: () => void;
  changeHandler: (name: string, value: any) => void;
}

interface IProps<
  ReportType extends ITableReport,
  SearchParam,
> extends IExtraTypeUIProps<ReportType> {
  chartGetter: (searchParams: SearchParam) => Promise<Graph_Dto[] | Graph_Dto>;
  withDate?: boolean;
  SearchParamUI?: (props: ISPProps<SearchParam>) => JSX.Element;
  title?: string;
}

const CellWithChart = <ReportType extends ITableReport, SearchParam>({
  cellType = "Price",
  headerName,
  row,
  SearchParamUI,
  chartGetter,
  withDate,
  title = "",
  translateHeaderName,
}: IProps<ReportType, SearchParam>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [graphData, setGraphData] = useState<Graph_Dto[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    clientSearchParamChangeHandler,
    clientSearchParams,
    onSubmit,
    serverSearchParams,
  } = useSearchParam({ initialSearchParam: {} as SearchParam });

  const getChartHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleCloseMenu();
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen) return;
    setLoading(true);
    chartGetter(serverSearchParams)
      .then((resp) => {
        setGraphData(Array.isArray(resp) ? resp : [resp]);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [serverSearchParams, isModalOpen]);

  const rightClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setAnchorEl(null);
    setIsTooltipOpen(false);
  };

  const getModalUI = () => (
    <div className="h-full w-full min-h-0 flex flex-row items-stretch gap-5 px-4">
      {SearchParamUI ? (
        <SearchParamUI
          changeHandler={clientSearchParamChangeHandler}
          onSubmit={onSubmit}
          searchParams={clientSearchParams}
        />
      ) : (
        <></>
      )}
      {title ? <span className="text-lg">{title}</span> : null}
      <div className="chart-fill-modal">
        {loading ? (
          <div className="flex h-full w-full items-center justify-center" role="status" aria-label="در حال دریافت نمودار">
            <CircularProgress />
          </div>
        ) : (
          <WrappedTimeSeriesChart dataset={graphData} withDate={withDate} className="h-full w-full" />
        )}
      </div>
    </div>
  );

  return (
    <>
      {isModalOpen ? (
        <Modal
          onClose={() => {
            handleCloseMenu();
            setIsModalOpen(false);
          }}
          fullScreen
          title={title || translateHeaderName || headerName}
          UI={getModalUI()}
          modalBoxClassName="!z-[9999]"
        />
      ) : (
        <></>
      )}
      <Tooltip
        title="نمودار"
        className="!w-full !relative"
        open={isTooltipOpen}
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
      >
        <div onContextMenu={rightClickHandler} className="ltr">
          <Cell clonedRow={row} headerName={headerName} type={cellType} />
          {isMenuOpen ? (
            <Menu
              className="!z-[9998]"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleCloseMenu}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={getChartHandler}>نمودار</MenuItem>
            </Menu>
          ) : (
            <></>
          )}
        </div>
      </Tooltip>
    </>
  );
};

export default CellWithChart;
